import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Music2, Mail, Instagram, Twitter, Music, Send } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { submitContact, subscribeNewsletter } from '../services/api';

const Footer = ({ artistData }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [newsletter, setNewsletter] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);

  if (!artistData) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await submitContact(formData);
      toast({
        title: 'Message Sent ✨',
        description: response.message,
      });
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubscribing(true);
    
    try {
      const response = await subscribeNewsletter(newsletter);
      toast({
        title: 'Subscribed! 🎵',
        description: response.message,
      });
      setNewsletter('');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to subscribe. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <footer className="relative overflow-hidden bg-black">
      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 relative z-10">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6">
              <Send className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm font-medium text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>Get in Touch</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-bold mb-6 text-white" style={{ fontFamily: 'Cinzel, serif' }}>
              Connect & Collaborate
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Let's create something beautiful together
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold mb-6 text-white" style={{ fontFamily: 'Cinzel, serif' }}>
                  Let's Talk
                </h3>
                <p className="text-gray-400 leading-relaxed mb-8">
                  Whether you're interested in collaborations, bookings, or just want to connect about music and art, I'd love to hear from you.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-4 p-4 bg-[#D4AF37]/5 border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-colors">
                  <div className="p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                    <Mail className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <a href={`mailto:${artistData.email}`} className="text-gray-300 hover:text-[#D4AF37] transition-colors">
                    {artistData.email}
                  </a>
                </div>
              </div>
              
              {/* Social Links */}
              <div className="pt-8 border-t border-[#D4AF37]/20">
                <p className="text-sm font-semibold text-gray-500 mb-4 uppercase tracking-wider">
                  Follow the Journey
                </p>
                <div className="flex space-x-4">
                  <a 
                    href={artistData.social.spotify} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center bg-[#D4AF37]/10 border-2 border-[#D4AF37]/30 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all group"
                  >
                    <Music className="w-5 h-5 text-[#D4AF37] group-hover:text-black" />
                  </a>
                  <a 
                    href="#" 
                    className="w-12 h-12 flex items-center justify-center bg-[#D4AF37]/10 border-2 border-[#D4AF37]/30 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all group"
                  >
                    <Instagram className="w-5 h-5 text-[#D4AF37] group-hover:text-black" />
                  </a>
                  <a 
                    href="#" 
                    className="w-12 h-12 flex items-center justify-center bg-[#D4AF37]/10 border-2 border-[#D4AF37]/30 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all group"
                  >
                    <Twitter className="w-5 h-5 text-[#D4AF37] group-hover:text-black" />
                  </a>
                </div>
              </div>
              
              {/* Newsletter */}
              <div className="pt-8">
                <h4 className="text-lg font-semibold mb-4 text-white" style={{ fontFamily: 'Cinzel, serif' }}>
                  Stay in the Loop
                </h4>
                <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Your email"
                    value={newsletter}
                    onChange={(e) => setNewsletter(e.target.value)}
                    required
                    className="bg-black border-[#D4AF37]/20 text-white placeholder:text-gray-500 focus:border-[#D4AF37]"
                  />
                  <Button
                    type="submit"
                    className="bg-[#D4AF37] hover:bg-[#FFB547] text-black"
                    disabled={isSubscribing}
                  >
                    {isSubscribing ? 'Subscribing...' : 'Subscribe'}
                  </Button>
                </form>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="bg-[#D4AF37]/5 p-8 border-2 border-[#D4AF37]/20">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-black border-[#D4AF37]/20 text-white placeholder:text-gray-500 focus:border-[#D4AF37]"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-black border-[#D4AF37]/20 text-white placeholder:text-gray-500 focus:border-[#D4AF37]"
                  />
                </div>
                <div>
                  <Textarea
                    name="message"
                    placeholder="Your Message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="bg-black border-[#D4AF37]/20 text-white placeholder:text-gray-500 focus:border-[#D4AF37] resize-none"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#D4AF37] hover:bg-[#FFB547] text-black py-6 text-base font-semibold"
                  disabled={isSubmitting}
                >
                  <Send className="w-5 h-5 mr-2" />
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer Bottom */}
      <div className="border-t border-[#D4AF37]/20 py-8 relative z-10">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Music2 className="w-6 h-6 text-[#D4AF37]" />
                <div className="absolute inset-0 blur-lg bg-[#D4AF37] opacity-30"></div>
              </div>
              <span className="text-2xl font-bold artist-name text-white">
                {artistData.name}
              </span>
            </div>
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} All rights reserved. | Bridging Ancient & Modern Worlds
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
